import { faClone, faPlay, faTrash } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, IconButton, Tooltip } from '@mui/material';
import { ReactNode, useContext, useEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';

import {
  BacktestingContext,
  SKIP_VALIDATION_FOR_DEMO,
} from '../../../../providers/backtesting/backtesting-provider';
import { useStrategyForm } from '../../../../providers/backtesting/hooks/use-strategy-form';
import { StrategyFormData } from '../../../../providers/backtesting/types';

import { AccountInformationSection } from './sections/account-information';
import { EntryConditionsSection } from './sections/entry-conditions';
import { ExecutionSection } from './sections/execution';
import { ExitConditionsSection } from './sections/exit-conditions';
import { HedgingSection } from './sections/hedging';
import { PortfolioSection } from './sections/portfolio';
import { RollPositionsSection } from './sections/roll-positions';

import './styles.scss';
import { BacktestingInput } from './components/backtesting-input';
import { ConfirmModal } from '../confirm-modal';
import Utils from '../../../../utils/immersive_utils';

interface StrategyFormProps {
  strategyId: string;
  backtestId: string;
  defaults?: Partial<StrategyFormData>;
  // Optional slot rendered inside the form body, between the Strategy Name
  // field and the first section (Account Information). The Specification tab
  // uses it to drop the backtest-global config in as a native panel there.
  beforeSections?: ReactNode;
}

const MAX_LABEL_LENGTH = 70;

export const StrategyForm = ({
  strategyId,
  backtestId,
  defaults,
  beforeSections,
}: StrategyFormProps) => {
  const form = useStrategyForm(defaults);
  const {
    getActiveBacktest,
    clearAllStrategiesForBacktest,
    deleteStrategy,
    duplicateStrategy,
    registerStrategyForm,
    runBacktest,
    runStrategy,
    unregisterStrategyForm,
    updateStrategyDraft,
    syncStrategyLabel,
  } = useContext(BacktestingContext);

  const activeBacktest = getActiveBacktest();
  const strategyLabel =
    activeBacktest?.strategies.find((strategy) => strategy.id === strategyId)?.label ||
    'Trade Strategy';
  // A study must keep at least one backtest — deleting the last would strand the
  // shell in a zero-backtest dead-end. Disable delete when it's the only one.
  const canDelete = (activeBacktest?.strategies.length ?? 0) > 1;
  const isRunningBacktest =
    activeBacktest?.id === backtestId && activeBacktest.execution.status === 'loading';
  // Whether THIS variant is mid per-variant Run — drives the row's own spinner
  // without touching the study-wide loading state.
  const isThisStrategyRunning =
    activeBacktest?.execution.runningStrategyIds.includes(strategyId) ?? false;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = form;

  useEffect(() => {
    registerStrategyForm(strategyId, {
      backtestId,
      getValues: form.getValues,
      trigger: () => form.trigger(),
      setNameError: (message) => form.setError('name', { type: 'manual', message }),
      clearNameError: () => form.clearErrors('name'),
    });

    return () => unregisterStrategyForm(strategyId);
  }, [backtestId, form, registerStrategyForm, strategyId, unregisterStrategyForm]);

  useEffect(() => {
    updateStrategyDraft(backtestId, strategyId, form.getValues());

    const subscription = form.watch((_, { name: changedField }) => {
      updateStrategyDraft(backtestId, strategyId, form.getValues());

      if (changedField === 'name') {
        syncStrategyLabel(backtestId, strategyId, form.getValues('name') ?? '');
      }
    });

    return () => subscription.unsubscribe();
  }, [backtestId, form, strategyId, syncStrategyLabel, updateStrategyDraft]);

  const onSubmit = async () => {
    await runBacktest();
  };

  const handleFormSubmit = SKIP_VALIDATION_FOR_DEMO
    ? (e: React.FormEvent) => {
        e.preventDefault();
        void onSubmit();
      }
    : handleSubmit(onSubmit);

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const onCancel = () => {
    clearAllStrategiesForBacktest(backtestId);
  };

  const handleDeleteStrategy = () => {
    deleteStrategy(backtestId, strategyId);
  };

  const handleDuplicateStrategy = () => {
    duplicateStrategy(backtestId, strategyId, form.getValues());
  };

  const handleRunStrategy = () => {
    void runStrategy(backtestId, strategyId);
  };

  return (
    <FormProvider {...form}>
      <form
        className="strategy-form"
        onSubmit={handleFormSubmit}
        data-strategy-id={strategyId}
        data-backtest-id={backtestId}
      >
        <div className="strategy-form-header">
          <Tooltip title={strategyLabel.length >= MAX_LABEL_LENGTH ? strategyLabel : ''}>
            <h3 className="strategy-form-title">
              {Utils.truncateLabel(strategyLabel, MAX_LABEL_LENGTH)}
            </h3>
          </Tooltip>
          <div className="strategy-form-header-actions">
            <Tooltip title="Run this backtest">
              <span className="strategy-form-action-button-wrap">
                <IconButton
                  className="strategy-form-action-button"
                  type="button"
                  onClick={handleRunStrategy}
                  disabled={isThisStrategyRunning || isRunningBacktest}
                >
                  <FontAwesomeIcon icon={faPlay} className="icon" />
                  <span className="strategy-form-action-button-text">
                    {isThisStrategyRunning ? 'Running…' : 'Run'}
                  </span>
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Duplicate">
              <IconButton
                className="strategy-form-action-button"
                type="button"
                onClick={handleDuplicateStrategy}
              >
                <FontAwesomeIcon icon={faClone} className="icon" />
                <span className="strategy-form-action-button-text">Duplicate</span>
              </IconButton>
            </Tooltip>
            <Tooltip title={canDelete ? 'Delete' : 'A study must keep at least one backtest'}>
              <span className="strategy-form-action-button-wrap">
                <IconButton
                  className="strategy-form-action-button"
                  type="button"
                  onClick={handleDeleteStrategy}
                  disabled={!canDelete}
                >
                  <FontAwesomeIcon icon={faTrash} className="icon" />
                  <span className="strategy-form-action-button-text">Delete</span>
                </IconButton>
              </span>
            </Tooltip>
          </div>
        </div>

        <div className="strategy-form-body">
          <div className="field-group">
            <BacktestingInput
              label="Backtest Name*"
              placeholder="An easy-to-identify name"
              {...register('name')}
              errors={errors.name?.message}
              size="lg"
            />
          </div>

          {beforeSections}

          <AccountInformationSection />
          <ExecutionSection />
          <HedgingSection />
          <PortfolioSection />
          <EntryConditionsSection />
          <ExitConditionsSection />
          <RollPositionsSection />
        </div>

        <div className="strategy-form-footer">
          <Button
            className="btn-cancel"
            type="button"
            onClick={() => setIsConfirmOpen(true)}
            disabled={isRunningBacktest}
          >
            Cancel
          </Button>
          <Button className="btn-run" type="submit" disabled={isRunningBacktest}>
            {isRunningBacktest ? 'Running...' : 'Run Backtest'}
          </Button>
        </div>
      </form>
      <ConfirmModal
        open={isConfirmOpen}
        title="Are you sure you want to cancel?"
        description="All your strategies and entered data will be lost."
        cancelLabel="Cancel"
        confirmLabel="Cancel anyway"
        onCancel={() => setIsConfirmOpen(false)}
        onConfirm={onCancel}
      />
    </FormProvider>
  );
};
